import { If, pipe, removeEmptyKeys } from '@helo/utils'
import { Record, Value } from '@helo/utils'
import assert from 'assert'
import { isEqual } from 'lodash'
import { useCallback, useId, useMemo } from 'react'

import { useWidgetConfig } from '../../../widgets/WidgetRenderer/WidgetProvider'
import { useAppStore } from '../appStore'
import { pruneViewState } from './viewSlice.parts'

export const useViewWidgetState = <
  State extends Record<string, any>,
  SavedState extends Record<string, any> = State,
>({
  widgetId,
  state: defaultState,
  onRead,
  onWrite,
  persist = false, // default to false since many tables shouldn't persist. safety.
}: {
  widgetId?: string
  /** Initial default state */
  state: State
  onRead?: (state: SavedState) => State
  onWrite?: (state: State) => SavedState
  /** whether or not the state should persist to the url */
  persist?: boolean
}) => {
  const widgetConfig = useWidgetConfig()
  const contextWidgetId = widgetConfig?.state?.widgetId
  const finalWidgetId = contextWidgetId || widgetId

  assert(finalWidgetId, `widgetId is required for widget ${widgetConfig?.type}`)

  const shouldPersist = Boolean(persist && finalWidgetId)

  const {
    viewStoreWidgetState,
    savedViewName,
    savedViewId,
    baseWidgetStateFromStore,
  } = useAppStore(({ viewStore }) => ({
    viewStoreWidgetState: viewStore.getWidgetState(finalWidgetId),
    savedViewName: viewStore.savedView?.name,
    savedViewId: viewStore.savedView?.id,
    baseWidgetStateFromStore: viewStore.baseViewState.widgetStates.find(
      ({ widgetId }) => widgetId === finalWidgetId,
    ),
  }))

  const handleRead = useCallback(
    (state: SavedState): State =>
      onRead?.(state) ?? (state as unknown as State),
    [onRead],
  )

  const handleWrite = useCallback(
    (state: State) => onWrite?.(state) ?? (state as unknown as SavedState),
    [onWrite],
  )

  const initialDefaultState = useMemo((): SavedState => {
    const defaultSavedState = handleWrite(defaultState)

    const savedViewWidgetState = useAppStore
      .getState()
      .viewStore.baseViewState.widgetStates.find(
        ({ widgetId }) => widgetId === finalWidgetId,
      )

    const finalDefaultState = savedViewWidgetState
      ? ((savedViewWidgetState.state as SavedState) ?? ({} as SavedState))
      : defaultSavedState

    // Prune the state before setting it as baseWidgetState (remove empty keys)
    const prunedState = removeEmptyKeys(finalDefaultState)
    const shouldSetBaseState =
      !Value.isNullable(prunedState) && !Record.isEmptyRecord()(prunedState)
    setTimeout(() => {
      if (shouldSetBaseState) {
        useAppStore.getState().viewStore.setWidgetBaseState({
          widgetId: finalWidgetId,
          state: prunedState,
        })
      }
    }, 1)
    return finalDefaultState
  }, [])

  const state = useMemo(
    () =>
      handleRead({
        ...initialDefaultState,
        ...viewStoreWidgetState?.state,
      } as any),
    [viewStoreWidgetState?.state, initialDefaultState, handleRead],
  )

  // Base widget state: get from viewStore, process through onRead
  const baseWidgetState = useMemo(() => {
    const baseState = baseWidgetStateFromStore?.state as SavedState | undefined
    if (!baseState) {
      // If no base state in store, use the pruned initial default state
      const prunedState = removeEmptyKeys(initialDefaultState)
      return handleRead(prunedState as any)
    }
    return handleRead(baseState)
  }, [
    baseWidgetStateFromStore,
    savedViewId,
    savedViewName,
    initialDefaultState,
    handleRead,
  ])

  const setState = useCallback(
    (partialUpdateState?: Partial<State>) => {
      const existingState = useAppStore
        .getState()
        .viewStore.getWidgetState(finalWidgetId)?.state

      // remove if not passing
      if (!partialUpdateState) {
        useAppStore
          .getState()
          .viewStore.removeWidgetState(finalWidgetId, shouldPersist)
        if (shouldPersist) {
          const store = useAppStore.getState()
          const viewStateValue = pruneViewState(store.viewStore)
          store.persistence.persist({
            key: 'viewState',
            value: viewStateValue,
            where: 'url',
            until: 'navigate',
          })
        }
        return
      }

      // New saved state allows partial update. This creates the new state.
      const mergedState = {
        ...existingState,
        ...partialUpdateState,
      }

      const onWriteModifiedState = handleWrite(mergedState as any)

      // remove keys that match defaults
      const finalState = pruneState(
        onWriteModifiedState,
        defaultState,
        Boolean(savedViewName),
      )
      if (!finalState) {
        useAppStore
          .getState()
          .viewStore.removeWidgetState(finalWidgetId, shouldPersist)
        if (shouldPersist) {
          const store = useAppStore.getState()
          const viewStateValue = pruneViewState(store.viewStore)
          store.persistence.persist({
            key: 'viewState',
            value: viewStateValue,
            where: 'url',
            until: 'navigate',
          })
        }
        return
      }

      useAppStore.getState().viewStore.setWidgetState(
        {
          widgetId: finalWidgetId,
          state: finalState,
        },
        false,
      )
      if (shouldPersist) {
        const store = useAppStore.getState()
        const viewStateValue = pruneViewState(store.viewStore)
        if (viewStateValue !== undefined) {
          store.persistence.persist({
            key: 'viewState',
            value: viewStateValue,
            where: 'url',
            until: 'navigate',
          })
        }
      }
    },
    [finalWidgetId, shouldPersist],
  )
  return [state, setState, baseWidgetState] as const
}

const pruneState = <T extends Record<string, any>>(
  state: T,
  defaultState: any,
  isSavedView = false,
): Partial<T> | undefined => {
  const reducedKeys = Object.keys(state).reduce<Partial<T>>((prev, key) => {
    if (!isSavedView && isEqual(state[key], defaultState[key])) {
      return prev
    }

    return {
      ...prev,
      [key]: state[key],
    }
  }, {})
  // remove if empty
  return Record.isEmptyRecord()(reducedKeys) ? undefined : reducedKeys
}
